import { Controller, type ControllerProps } from 'react-hook-form'
import TextField, { type TextFieldProps } from '@mui/material/TextField'
import { type InputBaseComponentProps } from '@mui/material/InputBase'
import GridItem, { omitGridProps, type GridItemProps } from './GridItem'
import { PasswordField } from './PasswordField'

const MAX_LENGTH = 255
const maxLengthError = `Maximum ${MAX_LENGTH} characters`

const disallowNonNumericCharacters = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isNaN(parseFloat(event.key))) {
        event.preventDefault()
    }
}

const customPropKeys = ['noNegative', 'unsignedInt', 'rules', 'transform', 'data-test-id', 'inputProps'] as const

const omitCustomProps = <T extends Record<string, unknown>>(obj: T) => {
    const result = { ...obj }
    for (const key of customPropKeys) {
        delete result[key]
    }
    return result
}

export type TextInputProps<T = string, StoreAs = T | undefined> = TextFieldProps &
    GridItemProps & {
        name: ControllerProps['name']
        noNegative?: boolean
        unsignedInt?: boolean
        rules?: ControllerProps['rules']
        showPassword?: { show: boolean; id: string }
        transform?: { displayAs: (value: T) => string | number; storeAs: (value: T | '') => StoreAs }
        inputProps?: InputBaseComponentProps
        'data-test-id'?: string
    }

/**
 * Text input with react-hook-form Controller and responsive GridItem layout.
 * Supports text, number, email, password types with optional value transformation.
 */
const TextInput = <T = string, StoreAs = T | undefined>({
    unsignedInt = true,
    ...props
}: TextInputProps<T, StoreAs>) => {
    const { rules = {}, variant = 'outlined' } = props
    const maxLength = props.inputProps && 'maxLength' in props.inputProps ? props.inputProps.maxLength : MAX_LENGTH
    const onKeyPress = props.type === 'number' && unsignedInt ? disallowNonNumericCharacters : undefined
    const applyDisplayAs = (value: unknown): unknown =>
        value != null && value !== '' ? (props.transform?.displayAs(value as T) ?? value) : ''

    if (props.noNegative && !rules.min) {
        rules.min = { value: 0, message: 'Cannot be negative' }
    }
    // Same contract as Select: `required` is a validation rule, not just the label
    // asterisk. Guarded on explicit rules so callers with their own validate (e.g.
    // network-manager's wrapper) keep sole ownership of validation.
    if (props.required && !rules.required && !rules.validate) {
        rules.required = 'This field is required'
    }

    return (
        <GridItem {...props}>
            <Controller
                name={props.name}
                rules={rules}
                render={({ field, fieldState }) => {
                    const reachedMaxLength =
                        typeof maxLength === 'number' &&
                        typeof field.value === 'string' &&
                        field.value.length >= maxLength
                    const showError = fieldState.invalid || reachedMaxLength
                    const fieldError = reachedMaxLength ? maxLengthError : (fieldState.error?.message ?? '')
                    const muiProps: TextFieldProps & Pick<TextInputProps, 'showPassword'> = {
                        ...field,
                        ...omitCustomProps(omitGridProps(props as unknown as Record<string, unknown>)),
                        error: showError,
                        helperText: showError ? fieldError : props.helperText,
                        inputRef: field.ref,
                        value: applyDisplayAs(field.value),
                        label: props.hiddenLabel ? undefined : (props.label ?? props.name),
                        hiddenLabel: props.hiddenLabel,
                        fullWidth: true,
                        variant,
                        margin: 'normal',
                        autoComplete: props.autoComplete ?? 'off',
                        onKeyPress,
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                            const value =
                                props.type === 'number' ? e.currentTarget.valueAsNumber : e.currentTarget.value
                            if (typeof value === 'number' && isNaN(value)) {
                                field.onChange(props.transform ? props.transform.storeAs('') : '')
                            } else {
                                field.onChange(props.transform ? props.transform.storeAs(value as T) : value)
                            }
                        },
                        slotProps: {
                            ...props.slotProps,
                            htmlInput: {
                                ...(props.noNegative ? { min: '0' } : undefined),
                                ...props.inputProps,
                                maxLength,
                            },
                            inputLabel: {
                                shrink: field.value != null && field.value !== '',
                            },
                        },
                    }
                    if (props.type === 'number') {
                        muiProps.onWheel = (e) => e.target instanceof HTMLElement && e.target.blur()
                    }
                    const dataTestId = props['data-test-id']
                    return props.type === 'password' ? (
                        <PasswordField {...muiProps} data-test-id={dataTestId} />
                    ) : (
                        <TextField {...muiProps} data-test-id={dataTestId} />
                    )
                }}
            />
        </GridItem>
    )
}

export default TextInput
